import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Coins, Lock, Unlock, Send, FileDown, RefreshCw, Mail, X, Car, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const MONTHS = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const methodLabel = (m) => ({
  nessuno: "Base fisso",
  scaglioni: "Base + Scaglioni",
  percentuale: "Percentuale",
}[m] || m);

export default function CedoliniPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ cedolini: [], bonus_label: "Bonus", payroll_company_name: "" });
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailFormat, setEmailFormat] = useState("excel");
  const [smtpAccounts, setSmtpAccounts] = useState([]);
  const [smtpAccountId, setSmtpAccountId] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  // Label bonus (impostazione tenant)
  const [labelDraft, setLabelDraft] = useState("");
  const [labelBusy, setLabelBusy] = useState(false);
  // Ragione sociale usata nel PDF/Email cedolini
  const [companyDraft, setCompanyDraft] = useState("");
  const [companyOriginal, setCompanyOriginal] = useState("");
  const [companyBusy, setCompanyBusy] = useState(false);
  // Rimborsi dialog
  const [rimbOpen, setRimbOpen] = useState(false);
  const [rimbTech, setRimbTech] = useState(null); // {id, name, closed}
  const [rimbItems, setRimbItems] = useState([]);
  const [rimbBusy, setRimbBusy] = useState(false);
  // Premio produzione come rimborso km
  const [kmRateDraft, setKmRateDraft] = useState("");
  const [kmBusy, setKmBusy] = useState("");

  // Selezione tecnici per invio/export
  const [selectedIds, setSelectedIds] = useState(new Set());
  const toggleSelected = (id) => setSelectedIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectAll = () => setSelectedIds((s) => {
    const list = (data.cedolini || []).map((c) => c.technician_id);
    if (s.size === list.length) return new Set();
    return new Set(list);
  });
  useEffect(() => {
    // reset selezione quando cambia il periodo
    setSelectedIds(new Set());
  }, [year, month]);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, sett] = await Promise.all([
        api.get("/cedolini", { params: { year, month } }),
        api.get("/settings").catch(() => ({ data: {} })),
      ]);
      setData(data);
      setLabelDraft(data.bonus_label || "Bonus");
      setKmRateDraft(data.default_km_rate || "");
      const company = sett?.data?.payroll_company_name || "";
      setCompanyDraft(company);
      setCompanyOriginal(company);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);
  useEffect(() => {
    api.get("/smtp-accounts").then((r) => setSmtpAccounts(r.data || [])).catch(() => setSmtpAccounts([]));
  }, []);

  const totals = useMemo(() => {
    const list = data.cedolini || [];
    return {
      total_netto: list.reduce((s, c) => s + Number(c.netto_totale ?? c.netto ?? 0), 0),
      total_base: list.reduce((s, c) => s + Number(c.base || 0), 0),
      total_bonus: list.reduce((s, c) => s + (c.bonus_as_km ? 0 : Number(c.bonus || 0)), 0),
      total_km: list.reduce((s, c) => s + Number(c.km_reimbursement || 0), 0),
      total_rimborsi: list.reduce((s, c) => s + Number(c.rimborsi_total || 0), 0),
      total_prod: list.reduce((s, c) => s + Number(c.produzione_lorda || 0), 0),
      count: list.length,
      closed: list.filter((c) => c.closed).length,
    };
  }, [data]);

  const saveLabel = async () => {
    setLabelBusy(true);
    try {
      await api.patch("/settings", { payroll_bonus_label: labelDraft || "Bonus" });
      toast.success("Etichetta bonus aggiornata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setLabelBusy(false); }
  };

  const saveCompany = async () => {
    setCompanyBusy(true);
    try {
      await api.patch("/settings", { payroll_company_name: companyDraft || "" });
      toast.success("Ragione sociale aggiornata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setCompanyBusy(false); }
  };

  const openRimborsi = async (tech) => {
    setRimbTech({ id: tech.technician_id, name: tech.technician_name, closed: !!tech.closed });
    setRimbItems([]);
    setRimbOpen(true);
    try {
      const { data } = await api.get("/cedolini/rimborsi", { params: { technician_id: tech.technician_id, year, month } });
      setRimbItems(data.items || []);
    } catch { /* ignore */ }
  };
  const addRimb = () => setRimbItems((p) => [...p, { id: `r_${Date.now()}`, name: "", amount: 0 }]);
  const updateRimb = (id, patch) => setRimbItems((p) => p.map((x) => x.id === id ? { ...x, ...patch } : x));
  const removeRimb = (id) => setRimbItems((p) => p.filter((x) => x.id !== id));
  const saveRimborsi = async () => {
    if (!rimbTech) return;
    setRimbBusy(true);
    try {
      await api.put("/cedolini/rimborsi", {
        technician_id: rimbTech.id, year, month,
        items: rimbItems.map((r) => ({ id: r.id, name: r.name || "", amount: Number(r.amount) || 0 })),
      });
      toast.success("Rimborsi salvati");
      setRimbOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setRimbBusy(false); }
  };

  const download = async (kind) => {
    try {
      const params = { year, month };
      if (selectedIds.size > 0) params.technician_ids = Array.from(selectedIds).join(",");
      const res = await api.get(`/cedolini/${kind}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const ext = kind === "pdf" ? "pdf" : "xlsx";
      a.href = url;
      a.download = `Cedolini_${year}_${String(month).padStart(2, "0")}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };

  const saveKmSettings = async (patch) => {
    try {
      await api.patch("/settings", patch);
      toast.success("Impostazioni rimborso km salvate");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    }
  };

  const generateKmSheets = async () => {
    const list = (data.cedolini || []).filter((c) => c.bonus_as_km);
    const res = { ok: 0, no_rate: [], skipped: 0, err: 0 };
    for (let i = 0; i < list.length; i++) {
      setKmBusy(`${i + 1}/${list.length}`);
      try {
        const { data: r } = await api.post("/mileage-sheets/auto", { technician_id: list[i].technician_id, year, month }, { timeout: 180000 });
        if (r.status === "ok") res.ok += 1;
        else if (r.status === "no_rate") res.no_rate.push(r.technician_name);
        else res.skipped += 1;
      } catch { res.err += 1; }
    }
    setKmBusy("");
    if (res.ok) toast.success(`Schede km generate/aggiornate: ${res.ok}`);
    if (res.no_rate.length) toast.warning(`Manca il €/km per: ${res.no_rate.join(", ")} — imposta il €/km predefinito`);
    if (res.err) toast.error(`${res.err} schede non generate`);
    if (!list.length) toast.info("Nessun tecnico con premio da trasformare in rimborso km");
    return res;
  };

  const closeMonth = async () => {
    if (!window.confirm(`Chiudi definitivamente i cedolini di ${MONTHS[month]} ${year}? Gli importi verranno congelati.`)) return;
    setCloseBusy(true);
    try {
      if (data.bonus_as_km) await generateKmSheets();
      const { data: d } = await api.post("/cedolini/close", { year, month });
      toast.success(`Chiusi ${d.closed} cedolini di ${MONTHS[month]} ${year}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore chiusura");
    } finally { setCloseBusy(false); }
  };

  const reopenMonth = async () => {
    if (!window.confirm(`Riaprire i cedolini di ${MONTHS[month]} ${year}? Verranno rimossi i valori congelati e ricalcolati sui dati attuali.`)) return;
    setCloseBusy(true);
    try {
      const { data } = await api.post("/cedolini/reopen", { year, month });
      toast.success(`Riaperti ${data.reopened} cedolini`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore riapertura");
    } finally { setCloseBusy(false); }
  };

  const openEmail = () => { setEmailOpen(true); setEmailFormat("excel"); };
  const sendEmail = async () => {
    if (!emailTo || !emailTo.includes("@")) return toast.error("Inserisci un'email valida");
    setEmailBusy(true);
    try {
      const payload = {
        year, month, to: emailTo, format: emailFormat,
        smtp_account_id: smtpAccountId || null,
      };
      if (selectedIds.size > 0) payload.technician_ids = Array.from(selectedIds);
      await api.post("/cedolini/send-email", payload);
      const suffix = selectedIds.size > 0 ? ` (${selectedIds.size} tecnici selezionati)` : "";
      toast.success(`Email inviata allo studio paghe${suffix}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore invio");
    } finally { setEmailBusy(false); }
  };

  return (
    <div className="space-y-5 p-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
            <Coins className="h-6 w-6 text-emerald-600" />
            Cedolini
          </h1>
          <p className="text-xs text-slate-500">Calcolo automatico dei cedolini mensili dei tecnici in base a base fisso + metodo di incentivazione.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm" data-testid="cedolini-refresh">
            <RefreshCw className="mr-1 h-3 w-3" /> Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={() => download("excel")} className="rounded-sm" data-testid="cedolini-excel">
            <FileDown className="mr-1 h-3 w-3" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => download("pdf")} className="rounded-sm" data-testid="cedolini-pdf">
            <FileDown className="mr-1 h-3 w-3" /> PDF
          </Button>
          <Button size="sm" onClick={openEmail} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="cedolini-email">
            <Mail className="mr-1 h-3 w-3" /> Invia allo studio paghe
          </Button>
        </div>
      </div>

      {/* Filtri periodo + Etichetta bonus + Ragione sociale */}
      <div className="rounded-sm border border-slate-200 bg-white p-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <div>
            <Label>Mese</Label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="mt-2 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
              data-testid="cedolini-month"
            >
              {MONTHS.slice(1).map((label, idx) => (
                <option key={idx + 1} value={idx + 1}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Anno</Label>
            <Input
              type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)}
              className="rounded-sm font-mono" data-testid="cedolini-year"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Etichetta bonus per studio paghe</Label>
            <div className="mt-2 flex gap-2">
              <Input
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                placeholder="es. Trasferta Italia"
                className="rounded-sm"
                data-testid="cedolini-bonus-label"
              />
              <Button
                onClick={saveLabel}
                disabled={labelBusy || labelDraft === data.bonus_label}
                size="sm"
                className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC] whitespace-nowrap"
                data-testid="cedolini-save-label"
              >
                {labelBusy ? "…" : "Salva"}
              </Button>
            </div>
          </div>
          <div className="md:col-span-2 flex items-end justify-end gap-2">
            {totals.closed < totals.count ? (
              <Button onClick={closeMonth} disabled={closeBusy || totals.count === 0} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cedolini-close">
                <Lock className="mr-1 h-3 w-3" /> Chiudi mese ({totals.count - totals.closed} da chiudere)
              </Button>
            ) : (
              <Button onClick={reopenMonth} disabled={closeBusy} variant="outline" className="rounded-sm border-amber-500 text-amber-700" data-testid="cedolini-reopen">
                <Unlock className="mr-1 h-3 w-3" /> Riapri mese
              </Button>
            )}
          </div>
        </div>
        <div>
          <Label>Ragione sociale per oggetto email / intestazione PDF</Label>
          <div className="mt-2 flex gap-2">
            <Input
              value={companyDraft}
              onChange={(e) => setCompanyDraft(e.target.value)}
              placeholder="es. Evolve Impianti Srls"
              className="rounded-sm"
              data-testid="cedolini-company-name"
            />
            <Button
              onClick={saveCompany}
              disabled={companyBusy || companyDraft === companyOriginal}
              size="sm"
              className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC] whitespace-nowrap"
              data-testid="cedolini-save-company"
            >
              {companyBusy ? "…" : "Salva"}
            </Button>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            L'oggetto email sarà: <b>"Prospetto Cedolini {MONTHS[month]} {year} — {companyDraft || "(nome tenant)"}"</b>. Lascia vuoto per usare il brand del tenant.
          </div>
        </div>
        <KmSettings
          enabled={!!data.bonus_as_km}
          onToggle={(v) => saveKmSettings({ payroll_bonus_as_km: v })}
          rate={kmRateDraft}
          setRate={setKmRateDraft}
          onSaveRate={() => saveKmSettings({ payroll_default_km_rate: Number(kmRateDraft) || 0 })}
          rateChanged={String(kmRateDraft) !== String(data.default_km_rate || "")}
          onGenerate={async () => { await generateKmSheets(); await load(); }}
          busy={kmBusy}
        />
      </div>

      {/* KPI Totali */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <div className="kpi-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Totale Netto Cedolini</div>
          <div className="mt-2 font-mono text-2xl font-bold text-emerald-600">€ {totals.total_netto.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Produzione Totale</div>
          <div className="mt-2 font-mono text-xl font-semibold text-slate-900">€ {totals.total_prod.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">di cui {data.bonus_label || "Bonus"}</div>
          <div className="mt-2 font-mono text-xl font-semibold text-blue-600">€ {totals.total_bonus.toFixed(2)}</div>
          {totals.total_km > 0 && <div className="mt-1 font-mono text-xs text-slate-600" data-testid="cedolini-kpi-km">+ Rimborso km € {totals.total_km.toFixed(2)}</div>}
        </div>
        <div className="kpi-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Rimborsi Spese</div>
          <div className="mt-2 font-mono text-xl font-semibold text-amber-600">€ {totals.total_rimborsi.toFixed(2)}</div>
        </div>
        <div className="kpi-card">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Stato Chiusura</div>
          <div className="mt-2 font-mono text-xl font-semibold text-slate-900">{totals.closed}/{totals.count} chiusi</div>
        </div>
      </div>

      {/* Tabella cedolini */}
      <div className="rounded-sm border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 p-3">
          <div className="text-sm font-semibold text-slate-900">Cedolini di {MONTHS[month]} {year}</div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2 rounded-sm border border-blue-300 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-800">
              <span data-testid="cedolini-selected-count">✓ {selectedIds.size} tecnic{selectedIds.size === 1 ? "o" : "i"} selezionat{selectedIds.size === 1 ? "o" : "i"}</span>
              <button onClick={() => setSelectedIds(new Set())} className="text-blue-600 hover:text-blue-800 underline">deseleziona tutti</button>
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={(data.cedolini || []).length > 0 && selectedIds.size === (data.cedolini || []).length}
                    ref={(el) => { if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < (data.cedolini || []).length; }}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 accent-[#0052FF]"
                    title="Seleziona tutti"
                    data-testid="cedolini-select-all"
                  />
                </th>
                <th className="p-3">Tecnico</th>
                <th className="p-3 text-right">Produzione Lorda</th>
                <th className="p-3">Metodo</th>
                <th className="p-3 text-right">Base</th>
                <th className="p-3 text-right">{data.bonus_as_km ? "Premio / Rimborso km" : (data.bonus_label || "Bonus")}</th>
                <th className="p-3 text-right">Rimborsi Spese</th>
                <th className="p-3 text-right">NETTO Cedolino</th>
                <th className="p-3">Stato</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="p-8 text-center text-slate-400">Caricamento…</td></tr>}
              {!loading && (data.cedolini || []).length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-slate-400">Nessun tecnico interno configurato per il cedolino</td></tr>
              )}
              {!loading && (data.cedolini || []).map((c) => (
                <tr key={c.technician_id} className={`border-b border-slate-100 hover:bg-slate-50 ${selectedIds.has(c.technician_id) ? "bg-blue-50" : ""}`} data-testid={`cedolino-row-${c.technician_id}`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.technician_id)}
                      onChange={() => toggleSelected(c.technician_id)}
                      className="h-4 w-4 accent-[#0052FF]"
                      data-testid={`cedolino-select-${c.technician_id}`}
                    />
                  </td>
                  <td className="p-3 font-medium">{c.technician_name}</td>
                  <td className="p-3 text-right font-mono">€ {Number(c.produzione_lorda).toFixed(2)}</td>
                  <td className="p-3">
                    <div className="flex flex-col">
                      <span>{methodLabel(c.method)}</span>
                      {c.method === "scaglioni" && (
                        <span className="text-[10px] text-slate-400">
                          Soglia €{c.config?.cedolino_soglia} · step €{c.config?.cedolino_step_produzione}/+€{c.config?.cedolino_step_bonus}
                          {c.config?.steps_counted != null && ` · ${c.config.steps_counted} step`}
                        </span>
                      )}
                      {c.method === "percentuale" && (
                        <span className="text-[10px] text-slate-400">
                          {c.config?.cedolino_bonus_percent}% della produzione
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">€ {Number(c.base).toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-blue-600" data-testid={`cedolino-bonus-${c.technician_id}`}>
                    € {Number(c.bonus).toFixed(2)}
                    {c.bonus_as_km && Number(c.bonus) > 0 && (
                      <div className={`mt-0.5 flex items-center justify-end gap-1 text-[10px] font-sans ${c.km_sheet ? "text-emerald-600" : "text-amber-600"}`} data-testid={`cedolino-km-status-${c.technician_id}`}>
                        <Car className="h-3 w-3" /> Rimborso km {c.km_sheet ? "· scheda ✓" : "· scheda da generare"}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right font-mono">
                    <div className="flex items-center justify-end gap-2">
                      <span className={Number(c.rimborsi_total || 0) > 0 ? "text-amber-600" : "text-slate-400"}>
                        € {Number(c.rimborsi_total || 0).toFixed(2)}
                      </span>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => openRimborsi(c)}
                        disabled={c.closed}
                        className="h-6 rounded-sm px-2 text-[10px]"
                        title={c.closed ? "Riapri il mese per modificare i rimborsi" : "Aggiungi/modifica rimborsi"}
                        data-testid={`cedolino-rimb-${c.technician_id}`}
                      >
                        {(c.rimborsi || []).length > 0 ? `${(c.rimborsi || []).length} voci` : "+ Aggiungi"}
                      </Button>
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono font-bold text-emerald-600">€ {Number(c.netto_totale ?? c.netto).toFixed(2)}</td>
                  <td className="p-3">
                    {c.closed ? (
                      <span className="inline-flex items-center gap-1 rounded-sm border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
                        <Lock className="h-3 w-3" /> Chiuso
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-sm border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                        <Unlock className="h-3 w-3" /> Aperto
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {(data.cedolini || []).length > 0 && (
              <tfoot className="bg-slate-100 font-bold">
                <tr>
                  <td className="p-3"></td>
                  <td className="p-3">TOTALI</td>
                  <td className="p-3 text-right font-mono">€ {totals.total_prod.toFixed(2)}</td>
                  <td></td>
                  <td className="p-3 text-right font-mono">€ {totals.total_base.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-blue-600">€ {totals.total_bonus.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-amber-600">€ {totals.total_rimborsi.toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-emerald-600">€ {totals.total_netto.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="rounded-sm border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
        💡 <b>Come funziona</b>: la produzione lorda viene calcolata dalle lavorazioni chiuse del mese (senza sconti, con quota condivisa se ci sono co-tecnici).
        Configura per ogni tecnico in <b>Tecnici & Utenti</b> il cedolino base e il metodo di incentivazione (Scaglioni o Percentuale).
        Chiudi il mese per congelare gli importi ed inviarli allo studio paghe.
      </div>

      {/* Dialog Rimborsi Spese */}
      <Dialog open={rimbOpen} onOpenChange={setRimbOpen}>
        <DialogContent className="rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle>Rimborsi spese — {rimbTech?.name}</DialogTitle>
            <DialogDescription>
              {MONTHS[month]} {year} · Aggiungi voci di rimborso spese (es. Rimborso Km, Trasferta, Vitto).
              Ogni voce viene sommata al netto cedolino e appare nell'email/PDF/Excel allo studio paghe.
            </DialogDescription>
          </DialogHeader>
          {rimbTech?.closed && (
            <div className="rounded-sm border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              ⚠️ Cedolino chiuso: riapri il mese per modificare i rimborsi.
            </div>
          )}
          <div className="space-y-2">
            {rimbItems.length === 0 && (
              <div className="rounded-sm border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-400">
                Nessun rimborso. Clicca "Aggiungi voce" per iniziare.
              </div>
            )}
            {rimbItems.length > 0 && (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-bold uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="p-2">Voce rimborso</th>
                    <th className="p-2 text-right">Importo €</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {rimbItems.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`rimb-row-${r.id}`}>
                      <td className="p-2">
                        <Input
                          value={r.name || ""}
                          onChange={(e) => updateRimb(r.id, { name: e.target.value })}
                          placeholder="es. Rimborso Km"
                          className="h-8 rounded-sm text-sm"
                          disabled={rimbTech?.closed}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          type="number" step="0.01"
                          value={r.amount}
                          onChange={(e) => updateRimb(r.id, { amount: Number(e.target.value) || 0 })}
                          className="h-8 w-32 rounded-sm text-right font-mono text-sm"
                          disabled={rimbTech?.closed}
                        />
                      </td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => removeRimb(r.id)}
                          className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                          disabled={rimbTech?.closed}
                          title="Rimuovi voce"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-100 font-bold">
                  <tr>
                    <td className="p-2">TOTALE</td>
                    <td className="p-2 text-right font-mono text-amber-600">
                      € {rimbItems.reduce((s, r) => s + (Number(r.amount) || 0), 0).toFixed(2)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
            <Button size="sm" variant="outline" onClick={addRimb} disabled={rimbTech?.closed} className="rounded-sm" data-testid="rimb-add">
              + Aggiungi voce
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRimbOpen(false)}>Annulla</Button>
            <Button onClick={saveRimborsi} disabled={rimbBusy || rimbTech?.closed} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="rimb-save">
              {rimbBusy ? "Salvo…" : "Salva rimborsi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog invio email */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Invia cedolini allo studio paghe</DialogTitle>
            <DialogDescription>
              Verrà inviata un'email con in allegato il prospetto netti cedolini di {MONTHS[month]} {year}.
            </DialogDescription>
          </DialogHeader>
          <div className={`rounded-sm border p-2 text-xs ${selectedIds.size > 0 ? "border-blue-300 bg-blue-50 text-blue-900" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
            {selectedIds.size > 0
              ? <>✓ Verranno inclusi <b>{selectedIds.size}</b> tecnic{selectedIds.size === 1 ? "o" : "i"} selezionat{selectedIds.size === 1 ? "o" : "i"}.</>
              : <>Verranno inclusi <b>tutti i {(data.cedolini || []).length} tecnici</b>. Per limitare l'invio, seleziona quelli desiderati dalla tabella tramite le checkbox.</>
            }
          </div>
          <div className="space-y-3">
            <div>
              <Label>Destinatario (email studio paghe)</Label>
              <Input
                type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="studiopaghe@esempio.it"
                className="rounded-sm" data-testid="cedolini-email-to"
              />
            </div>
            <div>
              <Label>Formato allegato</Label>
              <div className="mt-2 flex gap-4 text-sm">
                <label className="flex cursor-pointer items-center gap-2">
                  <input type="radio" checked={emailFormat === "excel"} onChange={() => setEmailFormat("excel")} />
                  <span>Excel (.xlsx)</span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input type="radio" checked={emailFormat === "pdf"} onChange={() => setEmailFormat("pdf")} />
                  <span>PDF</span>
                </label>
              </div>
            </div>
            <div>
              <Label>Account SMTP</Label>
              <select
                value={smtpAccountId}
                onChange={(e) => setSmtpAccountId(e.target.value)}
                className="mt-2 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                data-testid="cedolini-email-smtp"
              >
                <option value="">Predefinito (primo account SMTP)</option>
                {smtpAccounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.label || a.smtp_host} ({a.from_email})</option>
                ))}
              </select>
              <div className="mt-1 text-[11px] text-slate-500">
                Gli allegati richiedono un account SMTP configurato in Impostazioni → Email SMTP.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)}>Annulla</Button>
            <Button onClick={sendEmail} disabled={emailBusy} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="cedolini-email-send">
              <Send className="mr-1 h-3 w-3" /> {emailBusy ? "Invio…" : "Invia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KmSettings({ enabled, onToggle, rate, setRate, onSaveRate, rateChanged, onGenerate, busy }) {
  return (
    <div className="rounded-sm border border-blue-100 bg-blue-50/40 p-3" data-testid="cedolini-km-settings">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={onToggle} data-testid="cedolini-km-toggle" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Premio produzione come Rimborso chilometrico</div>
            <div className="text-[11px] text-slate-500">Metodo "Base + Scaglioni": base fissa + premio erogato come rimborso km, con scheda chilometrica automatica.</div>
          </div>
        </div>
        <div>
          <Label>€/km predefinito</Label>
          <div className="mt-1 flex gap-2">
            <Input type="number" step="0.0001" min="0" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="es. 0.45" className="w-28 rounded-sm font-mono" data-testid="cedolini-km-rate" />
            <Button size="sm" onClick={onSaveRate} disabled={!rateChanged} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="cedolini-km-rate-save">Salva</Button>
          </div>
        </div>
        {enabled && (
          <Button size="sm" variant="outline" onClick={onGenerate} disabled={!!busy} className="rounded-sm border-[#0052FF] text-[#0052FF]" data-testid="cedolini-km-generate">
            {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Car className="mr-1 h-3 w-3" />}
            {busy ? `Generazione ${busy}…` : "Genera/aggiorna schede km"}
          </Button>
        )}
      </div>
    </div>
  );
}
